import { type JSX, useState } from "react";

import {
  PaymentSourceIcon,
  SubscriptionPaymentMethodModal,
} from "features/payment-methods/components";
import useSubscriptionPaymentSource from "features/payment-methods/datahooks/useSubscriptionPaymentSource";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

export default function SubscriptionPaymentMethod({
  subscriptionId,
}: {
  subscriptionId: string;
}) {
  const { role } = useSelectedWorkspaceContext();
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);

  const { subscriptionPaymentSource, isLoadingSubscriptionPaymentSource } =
    useSubscriptionPaymentSource(subscriptionId);

  let content: JSX.Element;
  if (isLoadingSubscriptionPaymentSource) {
    content = (
      <>
        <Skeleton className="h-5 w-10" />
        <Skeleton className="h-5 w-24" />
      </>
    );
  } else if (subscriptionPaymentSource) {
    content = (
      <>
        <PaymentSourceIcon paymentSource={subscriptionPaymentSource} />
        <span className="text-sm-medium text-black-500">
          {subscriptionPaymentSource.type === "card"
            ? subscriptionPaymentSource.card.masked_number
            : subscriptionPaymentSource.paypal.email}
        </span>
      </>
    );
  } else {
    content = (
      <span className="text-sm-medium text-black-500">
        No credit card added
      </span>
    );
  }

  const editButtonComponent = (
    <Button
      variant="link-purple"
      size={isDesktop ? "sm" : "md"}
      className="ml-auto"
      onClick={() => setIsOpen(true)}
      disabled={isLoadingSubscriptionPaymentSource}
    >
      Edit
    </Button>
  );

  return (
    <>
      <div className="flex items-center gap-x-1">
        {content}
        <RenderIf condition={role === "owner"}>{editButtonComponent}</RenderIf>
      </div>

      <SubscriptionPaymentMethodModal
        subscriptionId={subscriptionId}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        initialPaymentSource={subscriptionPaymentSource}
      />
    </>
  );
}
